import React, { useEffect, useRef, useState } from 'react';
import { sendContactForm, UserInfo, isValidEmail } from '../Utility/sharedFunctions';
import '../CSS/ContactUs.css';
import '../CSS/ContactPage.css';
import Reveal from './Reveal';

const INQUIRIES = [
  'Artificial Intelligence',
  'Data Engineering',
  'Generative AI',
  'Software Engineering',
  'Catalyst Suite',
  'General Inquiry',
];

const STEPS = [
  { title: 'Tell us your goal', text: 'Share what you want to achieve.' },
  { title: 'We respond', text: 'A specialist replies with next steps.' },
  { title: 'Scope a pilot', text: 'Agree a focused starting point.' },
];

const EMPTY: UserInfo = { name: '', email: '', phoneNumber: '', inquiry: '', message: '' };

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState<UserInfo>(EMPTY);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');
  const inquiryRef = useRef<HTMLSelectElement>(null);

  // Pre-rendered markup has no `selected` attribute on the placeholder, so sync the select once hydrated.
  useEffect(() => {
    if (inquiryRef.current) inquiryRef.current.value = '';
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (status !== 'idle' && status !== 'sending') setStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(formData.email)) {
      setStatus('error');
      setFeedback('Please enter a valid email address.');
      return;
    }
    setStatus('sending');
    setFeedback('');
    try {
      await sendContactForm(formData);
      setStatus('success');
      setFeedback("Thank you! Your message has been sent. We'll get back to you soon.");
      setFormData(EMPTY);
    } catch (error) {
      setStatus('error');
      setFeedback("We couldn't send your message just now. Please try again, or email info@zemanticsllc.com.");
    }
  };

  return (
    <div className="contact-us-page ct-page">
      <div className="ct-hero">
        <div className="ct-hero-content">
          <h1 className="ct-hero-title">Reach Out to Us Today!</h1>
          <p className="ct-hero-subtitle">
            Our team is ready to help you move forward with the right solution. Let us know a bit about you, and we&apos;ll connect shortly!
          </p>
        </div>
      </div>

      <div className="ct-section">
        <Reveal className="ct-container">
          <div className="ct-layout">
            {/* Details */}
            <div className="ct-info">
              <h2 className="ct-info-title">Get in touch</h2>
              <dl className="ct-details">
                <div>
                  <dt>Email</dt>
                  <dd>
                    <a href="mailto:info@zemanticsllc.com">info@zemanticsllc.com</a>
                  </dd>
                </div>
                <div>
                  <dt>Office</dt>
                  <dd>
                    <address>
                      125 Village Blvd
                      <br />
                      Princeton NJ 08540
                    </address>
                  </dd>
                </div>
              </dl>

              <h3 className="ct-steps-title">What happens next</h3>
              <ol className="ct-steps">
                {STEPS.map((step, i) => (
                  <li key={step.title} style={{ ['--i' as string]: i } as React.CSSProperties}>
                    <span className="ct-step-number">{i + 1}</span>
                    <div>
                      <strong>{step.title}</strong>
                      <span>{step.text}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Form */}
            <form className="ct-form" onSubmit={handleSubmit} noValidate={false}>
              <h2 className="ct-form-title">Fill out the form</h2>

              <div className="ct-field">
                <label htmlFor="ct-name">Your name</label>
                <input id="ct-name" name="name" type="text" autoComplete="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="ct-row">
                <div className="ct-field">
                  <label htmlFor="ct-email">Email</label>
                  <input id="ct-email" name="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange} required />
                </div>
                <div className="ct-field">
                  <label htmlFor="ct-phone">Phone</label>
                  <input id="ct-phone" name="phoneNumber" type="tel" autoComplete="tel" value={formData.phoneNumber} onChange={handleChange} required />
                </div>
              </div>
              <div className="ct-field">
                <label htmlFor="ct-inquiry">Inquiry</label>
                <select id="ct-inquiry" ref={inquiryRef} name="inquiry" value={formData.inquiry} onChange={handleChange} required>
                  <option value="" disabled>
                    Select an inquiry
                  </option>
                  {INQUIRIES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ct-field">
                <label htmlFor="ct-message">Message</label>
                <textarea id="ct-message" name="message" rows={5} value={formData.message} onChange={handleChange} required />
              </div>

              <button type="submit" className="ct-submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending...' : 'Make a Request'}
              </button>

              <p className={`ct-feedback ${status === 'success' ? 'ct-success' : ''}${status === 'error' ? 'ct-error' : ''}`} role="status" aria-live="polite">
                {feedback}
              </p>
            </form>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default ContactUs;
